import React, { Component } from 'react';
import styles from './flexSetImagesGrid.scss';
import { IFileData } from '../../../articleTileGrid/article-tile/article-tile.d';
import classNames from 'classnames';
import TabNavigationHelper from '../../../../Common/tabNavigationHelper';
import IconArrowSmallLeft from '../../../../Assets/svg/icon_arrow_small_left';
import IconArrowSmallRight from '../../../../Assets/svg/icon_arrow_small_right';

export default class FlexSetImagesGrid extends Component<
    {
        on_hover: boolean, 
        detailsPageLink: string,
        articleName: string,
        isMobile: boolean
        preventDefaultEvents,
        categoryName: string,
        images: IFileData[],
        gratisIndex: number[],
    }, 
    {
        currentPage: number,
        totalPage: number,
    }> {
    
    private readonly slideShowRef = React.createRef<HTMLDivElement>();
    private readonly slidesRef = React.createRef<HTMLDivElement>();
    private tabNav: TabNavigationHelper;
    private MaxImagesPrePage = 2;

    constructor(props) {
        super(props);
        this.tabNav = TabNavigationHelper.instance;
        this.state = {
            currentPage: 1,
            totalPage: Math.ceil(this.props.images.length / this.MaxImagesPrePage),
        };
    }

    componentDidMount(): void {
        // firs component init, it helps to avoid transition during page load:
        if (this.slideShowRef.current) {
            this.slideShowRef.current.classList.add(styles.use_animation);
            this.tabNav.setGroupAtt(this.slideShowRef.current);
        }
    }

    private gridStyles(totalCount: number): string {
        const baseStyle = styles.image_grid;
        let addOn = '';
        switch (totalCount) {
            case 1: { 
                addOn = styles.singal;
                break;
            }
            case 2: {
                addOn = styles.double;
                break;
            }
            case 3: {
                addOn = styles.tripple;
                break;
            }
        }
        return [baseStyle, addOn].join(' ');
    }

    private imageStyles(imageIndex: number): string {
        const baseStyles = [classNames(styles.image_cell), imageIndex.toString()].join(' ');
        return baseStyles;
    }

    private getImageSrcSet(imgUrl: string ): string {        
        // DPR related subfix
        const subFix = this.props.isMobile ? ' 1200w,' : ' 768w,';

        // up to 768:
        const path_768 = imgUrl.replace('ArticleTileV3', 'TileImage768') + subFix;
        
        // up to HD:
        const path_hd = imgUrl + ' 1800w'

        return path_768 + ' ' + path_hd;
    }

    private getAltTagValue(imageTypeMlt: string): string {
        const categoryName = this.props.categoryName ? ', ' + this.props.categoryName: '';
        // const colorName = this.props.colorName ? ', ' + this.props.colorName : '';
        // return imageTypeMlt + categoryName + ', ' + this.props.articleName + colorName;
        return imageTypeMlt + categoryName + ', ' + this.props.articleName;
    }

    private getGridStyle(): React.CSSProperties {
        const count = Math.ceil(this.props.images.length / 2);
        return { gridTemplateColumns: `repeat(${count}, 50%)` }
    }

    private createSlideShow(): JSX.Element {
        return (
            <div
                className={this.gridStyles(this.props.images.length)}
                data-testid='slides'
                ref={this.slidesRef}
                style={this.getGridStyle()}
            >
                {this.props.images.map((img, imageIndex) => {
                    const tmpIndex = imageIndex + 1;
                    return (
                        <div className={classNames(this.imageStyles(tmpIndex))} key={tmpIndex}>
                            {
                                this.props.gratisIndex.includes(tmpIndex) &&
                                <div className={styles.gratis}> ###GRATIS### </div>
                            }
                            < img src={img.fullPath}
                                srcSet={this.getImageSrcSet(img.fullPath)}
                                loading='lazy'
                                key={tmpIndex}
                                decoding={'sync'}
                                data-index={tmpIndex}
                                alt={this.getAltTagValue(img.imageTypeMlt)}
                                data-testid={'slide'} >
                            </img>
                        </div>
                    );
                })}
            </div>
        );
    }

    private createMobileView(): JSX.Element {
        const displayMore = this.props.images.length > 4;
        const displayImages = displayMore ? this.props.images.slice(0, 3) : this.props.images;
        return (
            <div
                className={displayMore ? this.gridStyles(4) : this.gridStyles(displayImages.length)}
                data-testid='slides'
                ref={this.slidesRef}
                style={this.getGridStyle()}
            >
                {displayImages.map((img, imageIndex) => {
                    const tmpIndex = imageIndex + 1;
                    return (
                        <div className={classNames(this.imageStyles(tmpIndex))} key={tmpIndex}>
                            {
                                this.props.gratisIndex.includes(tmpIndex) &&
                                <div className={styles.gratis}> ###GRATIS### </div>
                            }
                            < img src={img.fullPath}
                                srcSet={this.getImageSrcSet(img.fullPath)}
                                loading='lazy'
                                key={tmpIndex}
                                decoding={'sync'}
                                data-index={tmpIndex}
                                alt={this.getAltTagValue(img.imageTypeMlt)}
                                data-testid={'slide'} >
                            </img>
                        </div>
                    );
                })}
                {
                    displayMore &&
                    <div className={classNames(this.imageStyles(4), styles.more_image_placeholder)}>
                            <div className={styles.more_image_label}>
                                {`+ ${this.props.images.length - displayImages.length}`}
                            </div>
                    </div>
                }
            </div>
        );
    }

    private swipeWithIndexDifference(index: number, event: React.MouseEvent | React.TouchEvent) {
        event.preventDefault();
        event.stopPropagation();
        if (this.slidesRef.current) {
            const viewWidth = this.slidesRef.current.clientWidth;
            const shift = Math.floor(viewWidth / 2);
            if (index > 0) {
                this.slidesRef.current.scrollBy({
                    top: 0,
                    left: +shift,
                    behavior: 'smooth'
                });
            } else {
                this.slidesRef.current.scrollBy({
                    top: 0,
                    left: -shift,
                    behavior: 'smooth'
                });
            }
            this.setState({ currentPage: this.state.currentPage + index });
        }
    }

    public render() {
        return (
            <div ref={this.slideShowRef} className={classNames(styles.flex_sets_image_container)}
                data-testid={'flex_sets_image_container'}>
                <div className={classNames(styles.swiper, {
                    [styles.swiper_hidden]: this.state.currentPage == 1 || !this.props.on_hover || this.props.isMobile
                }, styles.swipe_left)}
                    onClick={(event) => this.swipeWithIndexDifference(-1, event)} data-testid='swipe_left_click'
                    onTouchEnd={(event) => this.swipeWithIndexDifference(-1, event)}>
                    <IconArrowSmallLeft />
                </div>
                {this.props.isMobile ? this.createMobileView() :
                    this.createSlideShow()}
                <div className={classNames(styles.swiper, {
                    [styles.swiper_hidden]: this.state.totalPage <= 2 ||
                        this.state.currentPage + 1 == this.state.totalPage || !this.props.on_hover || this.props.isMobile
                }, styles.swipe_right)}
                    onClick={(event) => this.swipeWithIndexDifference(1, event)} data-testid='swipe_right_click'
                    onTouchEnd={(event) => this.swipeWithIndexDifference(1, event)}>
                    <IconArrowSmallRight />
                </div>
            </div>
        );
    }
}
